<template>
  <b-modal
    :visible="visible"
    size="xl"
    hide-header
    hide-footer
    scrollable
    body-class="p-0"
    @hidden="$emit('close')"
  >
    <!-- Header -->
    <div class="preview-header">
      <div class="preview-header-left">
        <div class="preview-icon">
          <i class="bx bx-show"></i>
        </div>
        <div>
          <h5 class="preview-title">{{ cardName }}</h5>
          <span class="preview-subtitle">Preview da ficha</span>
        </div>
      </div>
      <button type="button" class="preview-close" @click="$emit('close')">
        <i class="bx bx-x"></i>
      </button>
    </div>

    <!-- Toolbar -->
    <div class="preview-toolbar">
      <div class="toolbar-item">
        <label class="toolbar-label">Formulario</label>
        <div class="toolbar-select-wrapper">
          <select v-model="selectedFormId" class="toolbar-select">
            <option :value="null">Todos (Global)</option>
            <option
              v-for="form in forms"
              :key="form.id"
              :value="form.id"
            >{{ form.name }}</option>
          </select>
          <i class="bx bx-chevron-down toolbar-select-icon"></i>
        </div>
      </div>

      <div v-if="hasCompanionFields" class="toolbar-item">
        <label class="toolbar-label">Visualizar como</label>
        <div class="toggle-group">
          <button
            type="button"
            class="toggle-btn"
            :class="{ active: perspective === 'principal' }"
            @click="perspective = 'principal'"
          >
            <i class="bx bx-user"></i>
            <span>Principal</span>
          </button>
          <button
            type="button"
            class="toggle-btn"
            :class="{ active: perspective === 'companion' }"
            @click="perspective = 'companion'"
          >
            <i class="bx bx-group"></i>
            <span>Acompanhante</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="visibleTabs.length === 0" class="preview-empty">
      <i class="bx bx-info-circle"></i>
      <p>Nenhuma aba com campos para exibir.</p>
      <small>Adicione campos nas abas para visualizar o preview.</small>
    </div>

    <!-- Card Content -->
    <div v-else class="preview-content" role="tablist">
      <b-card
        v-for="(tab, tabIndex) in visibleTabs"
        :key="'preview-tab-' + tab.id"
        no-body
        class="mb-1 shadow-none"
      >
        <b-card-header header-tag="header" role="tab">
          <h6 class="m-0 d-flex justify-content-between align-items-center">
            <a
              :href="'#preview-collapse-' + tab.id"
              v-b-toggle="'preview-accordion-' + tab.id"
              class="text-dark"
            >
              {{ tab.name }}
            </a>
            <div class="d-flex align-items-center">
              <span class="badge badge-soft-primary mr-2">{{ getFilteredFields(tab).length }} campos</span>
              <i
                class="bx bx-chevron-down chevron-icon"
                v-b-toggle="'preview-accordion-' + tab.id"
              ></i>
            </div>
          </h6>
        </b-card-header>
        <b-collapse
          :id="'preview-accordion-' + tab.id"
          :visible="tabIndex === 0 || tab.is_expanded_default"
          accordion="preview-accordion"
          role="tabpanel"
        >
          <b-card-body>
            <form autocomplete="off">
              <div class="row">
                <div
                  v-for="field in getFilteredFields(tab)"
                  :key="'preview-field-' + field.id"
                  :class="'col-sm-' + getAdjustedSize(tab, field)"
                >
                  <component
                    :is="getFieldComponent(field)"
                    :id="'preview-' + field.id"
                    :value="''"
                    :label="getFieldLabel(field)"
                    :placeholder="field.placeholder || ''"
                    :options="getFieldOptions(field)"
                    class-input="col-sm-12"
                    :is-required="field.is_required"
                    :is-disabled="true"
                    :is-readonly="field.is_readonly"
                  />
                </div>
              </div>
            </form>
          </b-card-body>
        </b-collapse>
      </b-card>
    </div>
  </b-modal>
</template>

<script>
import http from '@/http'
import BaseInput from '@/components/formSchema/BaseInput'
import BaseInputEmail from '@/components/formSchema/BaseInputEmail'
import BaseInputPhone from '@/components/formSchema/BaseInputPhone'
import BaseSelect from '@/components/formSchema/BaseSelect'
import BaseRadio from '@/components/formSchema/BaseRadio'
import BaseTextArea from '@/components/formSchema/BaseTextArea'
import BaseInputDate from '@/components/formSchema/BaseInputDate'
import BaseInputCPF from '@/components/formSchema/BaseInputCPF'
import BaseInputCNPJ from '@/components/formSchema/BaseInputCNPJ'

export default {
  name: 'CardPreviewModal',
  components: {
    BaseInput: BaseInput,
    BaseInputEmail: BaseInputEmail,
    BaseInputPhone: BaseInputPhone,
    BaseSelect: BaseSelect,
    BaseRadio: BaseRadio,
    BaseTextArea: BaseTextArea,
    BaseInputDate: BaseInputDate,
    BaseInputCPF: BaseInputCPF,
    BaseInputCNPJ: BaseInputCNPJ
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    tabs: {
      type: Array,
      default: function () {
        return []
      }
    },
    cardName: {
      type: String,
      default: 'Ficha do Participante'
    }
  },
  data: function () {
    return {
      selectedFormId: null,
      perspective: 'principal',
      forms: [],
      states: [
        { value: 'AC', label: 'Acre' },
        { value: 'AL', label: 'Alagoas' },
        { value: 'AP', label: 'Amapa' },
        { value: 'AM', label: 'Amazonas' },
        { value: 'BA', label: 'Bahia' },
        { value: 'CE', label: 'Ceara' },
        { value: 'DF', label: 'Distrito Federal' },
        { value: 'ES', label: 'Espirito Santo' },
        { value: 'GO', label: 'Goias' },
        { value: 'MA', label: 'Maranhao' },
        { value: 'MT', label: 'Mato Grosso' },
        { value: 'MS', label: 'Mato Grosso do Sul' },
        { value: 'MG', label: 'Minas Gerais' },
        { value: 'PA', label: 'Para' },
        { value: 'PB', label: 'Paraiba' },
        { value: 'PR', label: 'Parana' },
        { value: 'PE', label: 'Pernambuco' },
        { value: 'PI', label: 'Piaui' },
        { value: 'RJ', label: 'Rio de Janeiro' },
        { value: 'RN', label: 'Rio Grande do Norte' },
        { value: 'RS', label: 'Rio Grande do Sul' },
        { value: 'RO', label: 'Rondonia' },
        { value: 'RR', label: 'Roraima' },
        { value: 'SC', label: 'Santa Catarina' },
        { value: 'SP', label: 'Sao Paulo' },
        { value: 'SE', label: 'Sergipe' },
        { value: 'TO', label: 'Tocantins' }
      ]
    }
  },
  mounted: function () {
    this.loadForms()
  },
  watch: {
    visible: function (newVal) {
      if (newVal) {
        this.loadForms()
      }
    }
  },
  computed: {
    hasCompanionFields: function () {
      if (!this.tabs || !Array.isArray(this.tabs)) return false
      for (var i = 0; i < this.tabs.length; i++) {
        var tab = this.tabs[i]
        if (!tab.fields) continue
        for (var j = 0; j < tab.fields.length; j++) {
          var fc = tab.fields[j].field_config
          if (fc && fc.display_for) return true
        }
      }
      return false
    },
    visibleTabs: function () {
      var self = this
      if (!self.tabs || !Array.isArray(self.tabs)) {
        return []
      }

      var filteredTabs = self.tabs.filter(function (tab) {
        if (!tab.fields || tab.fields.length === 0) {
          return false
        }
        var visibleFields = self.getFilteredFields(tab)
        return visibleFields && visibleFields.length > 0
      })

      return filteredTabs.sort(function (a, b) {
        var aHasStandard = a.fields.some(function (f) { return f.field_source === 'standard' })
        var bHasStandard = b.fields.some(function (f) { return f.field_source === 'standard' })
        if (aHasStandard && !bHasStandard) return -1
        if (!aHasStandard && bHasStandard) return 1
        return 0
      })
    }
  },
  methods: {
    getFieldComponent: function (field) {
      var self = this

      if (field.field_source === 'standard') {
        var standardComponentMap = {
          name: 'BaseInput',
          email: 'BaseInputEmail',
          cpf: 'BaseInputCPF',
          phone: 'BaseInputPhone',
          gender: 'BaseSelect',
          birth_date: 'BaseInputDate',
          address: 'BaseInput',
          city: 'BaseInput',
          state: 'BaseSelect',
          zip_code: 'BaseInput',
          country: 'BaseInput'
        }
        return standardComponentMap[field.standard_field_name] || 'BaseInput'
      }

      if (field.field_source === 'form_field' && field.form_field && field.form_field.field_type) {
        var componentName = field.form_field.field_type.component
        return self.mapComponentName(componentName || 'TextField')
      }

      if (field.field_source === 'internal' && field.field_config) {
        var fieldType = field.field_config.field_type
        var internalComponentMap = {
          text: 'BaseInput',
          textarea: 'BaseTextArea',
          number: 'BaseInput',
          select: 'BaseSelect',
          multiselect: 'BaseSelect',
          checkbox: 'BaseRadio',
          radio: 'BaseRadio',
          date: 'BaseInputDate',
          datetime: 'BaseInputDate',
          toggle: 'BaseRadio'
        }
        return internalComponentMap[fieldType] || 'BaseInput'
      }

      return 'BaseInput'
    },
    mapComponentName: function (componentName) {
      var componentMap = {
        'EmailField': 'BaseInputEmail',
        'TextField': 'BaseInput',
        'PhoneField': 'BaseInputPhone',
        'SelectField': 'BaseSelect',
        'RadioField': 'BaseRadio',
        'TextAreaField': 'BaseTextArea',
        'DateField': 'BaseInputDate',
        'CPFField': 'BaseInputCPF',
        'CNPJField': 'BaseInputCNPJ'
      }
      return componentMap[componentName] || 'BaseInput'
    },
    getFieldOptions: function (field) {
      var self = this

      if (field.field_source === 'standard') {
        if (field.standard_field_name === 'gender') {
          return [
            { value: 'M', label: 'Masculino' },
            { value: 'F', label: 'Feminino' },
            { value: 'O', label: 'Outro' }
          ]
        }
        if (field.standard_field_name === 'state') {
          return self.states
        }
      }

      if (field.field_source === 'form_field' && field.form_field) {
        return field.form_field.options || []
      }

      if (field.field_source === 'internal' && field.field_config && field.field_config.options) {
        return field.field_config.options
      }

      return []
    },
    getFieldLabel: function (field) {
      if (field.field_config && field.field_config.label_override) {
        return field.field_config.label_override
      }

      if (field.label) return field.label

      if (field.field_config && field.field_config.label) {
        return field.field_config.label
      }

      if (field.field_source === 'standard') {
        var labels = {
          name: 'Nome Completo',
          badge_name: 'Nome para Cracha',
          email: 'E-mail',
          cpf: 'CPF',
          phone: 'Telefone',
          gender: 'Genero',
          birth_date: 'Data de Nascimento',
          address: 'Endereco',
          city: 'Cidade',
          state: 'Estado',
          zip_code: 'CEP',
          country: 'Pais'
        }
        return labels[field.standard_field_name] || field.standard_field_name
      }

      if (field.field_source === 'form_field' && field.form_field) {
        return field.form_field.label || field.form_field.name
      }

      if (field.field_source === 'internal' && field.field_config) {
        return field.field_config.label || field.field_config.name
      }

      return 'Campo'
    },
    getFieldSize: function (field) {
      if (field.layout && field.layout.cols) {
        return field.layout.cols
      }
      return 12
    },
    getAdjustedSize: function (tab, field) {
      var self = this
      var filteredFields = self.getFilteredFields(tab)
      var allFields = tab.fields || []

      if (!self.hasCompanionFields || filteredFields.length === allFields.length) {
        return self.getFieldSize(field)
      }

      var originalSize = self.getFieldSize(field)
      var rowFields = []
      var rowTotal = 0

      for (var i = 0; i < filteredFields.length; i++) {
        var f = filteredFields[i]
        var fSize = self.getFieldSize(f)

        if (rowTotal + fSize > 12 && rowFields.length > 0) {
          var found = false
          for (var k = 0; k < rowFields.length; k++) {
            if (rowFields[k].id === field.id) { found = true; break }
          }
          if (found) break
          rowFields = []
          rowTotal = 0
        }

        rowFields.push(f)
        rowTotal += fSize
      }

      var inRow = false
      for (var j = 0; j < rowFields.length; j++) {
        if (rowFields[j].id === field.id) { inRow = true; break }
      }
      if (!inRow) return originalSize

      if (rowTotal < 12 && rowFields.length > 0) {
        var ratio = 12 / rowTotal
        var adjusted = Math.round(originalSize * ratio)
        if (adjusted > 12) adjusted = 12
        if (adjusted < 3) adjusted = 3
        return adjusted
      }

      return originalSize
    },
    loadForms: function () {
      var self = this
      http.get('/admin/forms', { params: { all: true } })
        .then(function (response) {
          self.forms = response.data.data || response.data || []
        })
        .catch(function (error) {
          console.error('[CardPreview] Erro ao carregar formularios:', error)
          self.forms = []
        })
    },
    getFilteredFields: function (tab) {
      var self = this
      if (!tab || !tab.fields) {
        return []
      }

      return tab.fields.filter(function (field) {
        if (self.selectedFormId) {
          if (field.field_source === 'form_field' && field.form_field) {
            if (field.form_field.form_id !== self.selectedFormId) {
              return false
            }
          }
        }

        if (self.hasCompanionFields) {
          var displayFor = field.field_config ? field.field_config.display_for : null
          if (displayFor) {
            if (self.perspective === 'principal' && displayFor === 'companion') {
              return false
            }
            if (self.perspective === 'companion' && displayFor === 'principal') {
              return false
            }
          }
        }

        return true
      })
    }
  }
}
</script>

<style scoped>
/* Header */
.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #f0f0f0;
}

.preview-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.preview-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #556ee6, #6c8cff);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 20px;
  flex-shrink: 0;
}

.preview-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0;
  line-height: 1.3;
}

.preview-subtitle {
  font-size: 12px;
  color: #8c8ca1;
}

.preview-close {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #8c8ca1;
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
}

.preview-close:hover {
  background: #f5f5f5;
  color: #1a1a2e;
}

/* Toolbar */
.preview-toolbar {
  display: flex;
  align-items: flex-end;
  gap: 20px;
  padding: 16px 24px;
  background: #fafbfc;
  border-bottom: 1px solid #f0f0f0;
}

.toolbar-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.toolbar-item:first-child {
  flex: 1;
  min-width: 0;
}

.toolbar-label {
  font-size: 11px;
  font-weight: 600;
  color: #8c8ca1;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
}

.toolbar-select-wrapper {
  position: relative;
}

.toolbar-select {
  appearance: none;
  width: 100%;
  padding: 8px 32px 8px 12px;
  font-size: 13px;
  color: #1a1a2e;
  background: #fff;
  border: 1px solid #e2e5ea;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s;
}

.toolbar-select:focus {
  outline: none;
  border-color: #556ee6;
  box-shadow: 0 0 0 3px rgba(85, 110, 230, 0.1);
}

.toolbar-select-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  color: #8c8ca1;
  pointer-events: none;
}

/* Toggle Group */
.toggle-group {
  display: flex;
  background: #e9ecef;
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
}

.toggle-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  color: #6c757d;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}

.toggle-btn i {
  font-size: 15px;
}

.toggle-btn:hover:not(.active) {
  color: #495057;
  background: rgba(255, 255, 255, 0.5);
}

.toggle-btn.active {
  background: #fff;
  color: #556ee6;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

/* Empty State */
.preview-empty {
  text-align: center;
  color: #8c8ca1;
  padding: 60px 24px;
}

.preview-empty i {
  font-size: 48px;
}

.preview-empty p {
  margin-top: 12px;
  font-size: 14px;
}

.preview-empty small {
  color: #adb5bd;
}

/* Content */
.preview-content {
  padding: 16px 24px 24px;
}

/* Chevron Icon Animation */
.chevron-icon {
  font-size: 18px;
  transition: transform 0.2s ease;
  color: #6c757d;
  cursor: pointer;
}

.chevron-icon.collapsed {
  transform: rotate(-90deg);
}
</style>
